import type { ComponentProps } from 'react'

import { Toast } from '@base-ui/react'
import styled, { css, keyframes } from 'styled-components'

import { SnackbarType } from './SnackbarType'

const pulseEven = keyframes`
	0%, 100% {
		scale: 1;
	}

	50% {
		scale: 1.04;
	}
`

const pulseOdd = keyframes`
	from {
		scale: 1;
	}

	50% {
		scale: 1.04;
	}

	to {
		scale: 1;
	}
`

const animation = css`
	--gap: 0.8rem;
	--height: var(--toast-frontmost-height, var(--toast-height));
	--offset-y: calc(var(--toast-offset-y) * -1 + (var(--toast-index) * var(--gap) * -1) + var(--toast-swipe-movement-y));
	--peek: 0.75rem;
	--scale: calc(max(0, 1 - (var(--toast-index) * 0.1)));
	--shrink: calc(1 - var(--scale));
	height: var(--height);
	transform: translateX(var(--toast-swipe-movement-x))
		translateY(
			calc(var(--toast-swipe-movement-y) - (var(--toast-index) * var(--peek)) - (var(--shrink) * var(--height)))
		)
		scale(var(--scale));
	transform-origin: bottom center;
	transition:
		transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
		opacity 0.5s,
		height 0.15s;
	z-index: calc(1000 - var(--toast-index));

	&:after {
		content: "";
		position: absolute;
		height: calc(var(--gap) + 1px);
		left: 0;
		top: 100%;
		width: 100%;
	}
`

export const SnackbarContainer = styled.div`
	${animation}
	background-color: ${({ theme }) => theme.color.secondary};
	border-radius: 8px;
	bottom: 0;
	box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 1px 18px 0 rgba(0, 0, 0, 0.12);
	box-sizing: border-box;
	color: ${({ theme }) => theme.color.textContrast};
	cursor: default;
	left: auto;
	margin-block: 0;
	margin-right: 0;
	max-width: 500px;
	min-width: 350px;
	padding-block: 14px;
	padding-inline: 16px;
	position: absolute;
	right: 0;
	text-align: center;
	user-select: none;
	width: fit-content;

	&[data-pulse='even'] {
		animation: ${pulseEven} 0.28s ease;
	}

	&[data-pulse='odd'] {
		animation: ${pulseOdd} 0.28s ease;
	}

	&[data-expanded='true'] {
		height: var(--toast-height);
		transform: translateX(var(--toast-swipe-movement-x)) translateY(var(--offset-y));
	}

	&[data-transition-status='starting'],
	&[data-transition-status='ending'] {
		transform: translateY(150%);
	}

	&[data-limited='true'],
	&[data-ending='true'] {
		opacity: 0;
	}

	&[data-ending='true'][data-swipe-direction='up'] {
		transform: translateY(calc(var(--toast-swipe-movement-y) - 150%));
	}

	&[data-ending='true'][data-swipe-direction='left'] {
		transform: translateX(calc(var(--toast-swipe-movement-x) - 150%)) translateY(var(--offset-y));
	}

	&[data-ending='true'][data-swipe-direction='right'] {
		transform: translateX(calc(var(--toast-swipe-movement-x) + 150%)) translateY(var(--offset-y));
	}

	&[data-ending='true'][data-swipe-direction='down'] {
		transform: translateY(calc(var(--toast-swipe-movement-y) + 150%));
	}

	&[data-type='${SnackbarType.Error}'] {
		background-color: ${({ theme }) => theme.color.danger};
	}

	@media (max-width: 575px) {
		box-shadow: none;
		max-width: none;
		min-width: 0;
		width: 100%;
	}
`

type Props = ComponentProps<typeof Toast.Root>

export const Container = ({ toast, ...rest }: Props) => (
	<Toast.Root
		{...rest}
		toast={toast}
		render={(props, state) => (
			<SnackbarContainer
				{...props}
				data-ending={state.transitionStatus === 'ending'}
				data-expanded={state.expanded}
				data-limited={state.limited}
				data-pulse={toast.updateKey ? (toast.updateKey % 2 === 0 ? 'even' : 'odd') : undefined}
				data-swipe-direction={state.swipeDirection}
				data-transition-status={state.transitionStatus}
				data-type={toast.type}
			/>
		)}
	/>
)
